import {DataTransferObject} from './DataTransferObject';
import {TicketConfigurationDTO} from './TicketConfigurationDTO';
import {Order, OrderItem, TicketConfiguration} from '../Models';
import {AdditionalOrderItemConfigurationDTO} from './AdditionalOrderItemConfigurationDTO';

export class OrderDTO extends DataTransferObject {
  public orderId: number | string | null = null;
  public codes: Array<string> = [];
  public ticketConfigurations: Array<TicketConfigurationDTO> = [];
  public additionalOrderItemConfigurations: Array<AdditionalOrderItemConfigurationDTO> = [];
  public token: string | null = null;

  fromModel(order: Order): OrderDTO {
    const orderItemDTO = new OrderDTO();
    orderItemDTO.orderId = order.id;
    orderItemDTO.codes = order.codes;
    order.orderItems.forEach((orderItem) => {
      const ticketConfig = this.buildTicketConfiguration(orderItem);
      orderItemDTO.ticketConfigurations.push(ticketConfig);
    });
    order.additionalOrderItemConfigurations.forEach((orderItemConfig) => {
      const orderItemConfigDTO = new AdditionalOrderItemConfigurationDTO();
      orderItemDTO.additionalOrderItemConfigurations.push(orderItemConfigDTO.fromModel(orderItemConfig));
    });
    orderItemDTO.token = order.token;

    return orderItemDTO;
  }

  /**
   * Checks if a product exists in the product selection and adds the new quantity or pushes if it doesn't exist
   * @param ticketConfigurationDTO
   */
  addProduct(ticketConfigurationDTO: TicketConfigurationDTO) {
    let addDTO = ticketConfigurationDTO;
    let productExists: boolean = false;
    for (let i = 0; i < this.ticketConfigurations.length; i++) {
      const existingTicketConfigurationDTO = this.ticketConfigurations[i];
      if (this.ticketConfigurationExistsInProduct(existingTicketConfigurationDTO, ticketConfigurationDTO)) {
        if (ticketConfigurationDTO.isProductFamily) {
          addDTO = TicketConfigurationDTO.clone(ticketConfigurationDTO);
          productExists = false;
        } else {
          existingTicketConfigurationDTO.quantity += ticketConfigurationDTO.quantity;
          productExists = true;
        }
        break;
      }
    }

    if (!productExists) {
      this.ticketConfigurations.push(addDTO);
    }
  }

  removeProductByDTO(ticketConfigurationDTO: TicketConfigurationDTO): boolean {
    let productDeleted: boolean = false;

    for (let i = 0; i < this.ticketConfigurations.length; i++) {
      const existingTicketConfiguration = this.ticketConfigurations[i];
      if (this.ticketConfigurationExistsInProduct(existingTicketConfiguration, ticketConfigurationDTO)) {
        this.ticketConfigurations.splice(i, 1);
        productDeleted = true;
        break;
      }
    }

    return productDeleted;
  }

  removeConfiguration(ticketConfiguration: TicketConfiguration): TicketConfigurationDTO | null {
    let configDeleted: TicketConfigurationDTO | null = null;
    const comparisonDTO = (new TicketConfigurationDTO()).fromModel(ticketConfiguration);
    if (ticketConfiguration.isPromoted && ticketConfiguration.createdFrom) {
      comparisonDTO.id = ticketConfiguration.createdFrom;
    }

    for (let i = 0; i < this.ticketConfigurations.length; i++) {
      const existingTicketConfiguration = this.ticketConfigurations[i];
      if (this.ticketConfigurationExistsInProduct(existingTicketConfiguration, comparisonDTO)) {
        if (ticketConfiguration.isPromoted) {
          // for a promotion, just reduce the quantity
          existingTicketConfiguration.quantity -= comparisonDTO.quantity;
          configDeleted = existingTicketConfiguration;

          // remove the code from the cart
          if (ticketConfiguration.isPromoted && ticketConfiguration.promotion) {
            this.removeCode(ticketConfiguration.promotion.code);
          }

          if (existingTicketConfiguration.quantity <= 0) {
            configDeleted = this.ticketConfigurations.splice(i, 1).shift() || null;
          }
        } else {
          configDeleted = this.ticketConfigurations.splice(i, 1).shift() || null;
        }
        break;
      }
    }

    return configDeleted;
  }

  refreshCart(ticketConfigurations: Array<TicketConfiguration>) {
    const dtos = ticketConfigurations.map(tc => (new TicketConfigurationDTO()).fromModel(tc));
    const clone = [...dtos];
    dtos.forEach(dto => {
      if (dto.quantity === 0) {
        clone.splice(clone.indexOf(dto), 1);
      }
    });
    this.ticketConfigurations = clone;
    return;
  }

  addCode(code: string) {
    this.codes.push(code);
  }

  removeCode(code: string) {
    this.codes.forEach((existingCode: string, index: number) => {
      if (existingCode === code) {
        this.codes.splice(index, 1);
      }
    });
  }

  private ticketConfigurationExistsInProduct(existingTicket: TicketConfigurationDTO, newTicket: TicketConfigurationDTO): boolean {
    return existingTicket.id === newTicket.id;
  }

  private buildTicketConfiguration(orderItem): TicketConfigurationDTO {
    let ticketConfig = new TicketConfigurationDTO();
    ticketConfig.id = orderItem.id;
    ticketConfig.productId = orderItem.productId;
    ticketConfig.parentId = orderItem.parentId;
    ticketConfig.quantity = orderItem.quantity;
    ticketConfig.personalUse = orderItem.personalUse;
    ticketConfig.registrationRequired = orderItem.registrationRequired;
    if (orderItem.slotId) {
      ticketConfig.slotId = orderItem.slotId;
    }
    ticketConfig.subTicketConfigurations = this.getSubTicketConfigurationsFromOrderItem(orderItem);
    return ticketConfig;
  }

  private getSubTicketConfigurationsFromOrderItem(orderItem: OrderItem): Array<TicketConfigurationDTO> {
    const subConfigurations: Array<TicketConfigurationDTO> = [];
    orderItem.subOrderItems.forEach((soi) => {
      subConfigurations.push(this.buildTicketConfiguration(soi));
    });
    return subConfigurations;
  }
}
